// Diagram files: one PNG per diagram, the diagram's state embedded in it as
// the `cthDiagram` tEXt chunk (engine/png.js, shared with CTH Apps and Film
// Room). The picture IS the file, so it embeds anywhere as a plain image and
// reopens editable here.
import { pngReadDiagram, pngSetDiagram } from '../engine/png.js';
import { renderStateFlat, composeFrames } from '../engine/flat.js';
import { RINK_W, RINK_H, SEQ_GAP, loadImg } from '../engine/rink.js';

/** A file name that is safe on macOS, Windows and in a wikilink. */
export function safeName(s, fallback = 'Untitled diagram') {
  const clean = String(s ?? '').replace(/[\\/:*?"<>|#^[\]]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^\.+/, '');
  return clean || fallback;
}

export function canvasToBytes(canvas, type = 'image/png', quality) {
  return new Promise((res, rej) => canvas.toBlob(async (b) => {
    if (!b) { rej(new Error('The picture could not be rendered')); return; }
    res(new Uint8Array(await b.arrayBuffer()));
  }, type, quality));
}
export const canvasToBlob = (canvas) => new Promise((res) => canvas.toBlob(res, 'image/png'));

const toAB = (u8) => u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength);

const isRinkShaped = (st) => {
  const seq = st.seq || 1;
  return st.w === RINK_W && st.h === seq * RINK_H + (seq - 1) * SEQ_GAP;
};

/**
 * The file's bytes for a state: the picture (captions in their bands when on)
 * with the state embedded. Rendered from the STATE, not the live editor, so a
 * save that lands after the tab closed still writes the right picture.
 */
export async function diagramBytes(state, { scale = 1, captions = true } = {}) {
  const full = await renderStateFlat(state, scale);
  const pic = state.bg ? full : composeFrames(full, null, captions ? (state.rinkCaptions || []) : []);
  return pngSetDiagram(await canvasToBytes(pic), state);
}

function bytesToDataUrl(u8, mime) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = () => rej(fr.error);
    fr.readAsDataURL(new Blob([u8], { type: mime }));
  });
}
const MIME = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif' };

/**
 * A diagram from any picture's bytes. A PNG that carries a diagram opens as
 * that diagram; any other picture becomes a PHOTO-BACKED diagram (the picture
 * as the background, nothing drawn yet): the web app's import rule.
 */
export async function stateFromBytes(u8, ext = 'png') {
  const st = ext === 'png' ? pngReadDiagram(u8) : null;
  if (st && Array.isArray(st.elements)) {
    if (st.bg || isRinkShaped(st)) return { state: st, carried: true };
  }
  const dataUrl = await bytesToDataUrl(u8, MIME[ext] || 'image/png');
  const img = await loadImg(dataUrl);
  return { state: { v: 1, w: img.naturalWidth, h: img.naturalHeight, bg: dataUrl, seq: 1, elements: [] }, carried: false };
}
